/**
 * PHYSICAL-RISKS.THUNK.TS
 * Custom endpoints
 */
import { createAsyncThunk } from "@reduxjs/toolkit"
import { PhysicalRisk } from "./physical-risk.types"
import { RootState } from "@/core/root-reducer"
import physicalRiskStore from "./physical-risk.store"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { fetchBackend } from "@/core/apis/backend"

/**
 * Add population for one site
 */
export const addPopulation = createAsyncThunk(
  "physicalRisk/add-population",
  async (
    { PopulationId, PhysicalRiskId }: { PopulationId: string; PhysicalRiskId: string },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState
    dispatch(physicalRiskStore.actions.setStatus("STATUS_SAVING"))

    //Send data
    const response = await fetchBackend({
      url: "/physicalRisk/add-population",
      method: "POST",
      data: {
        PopulationId,
        PhysicalRiskId
      }
    })

    //Handle response
    dispatch(physicalRiskStore.actions.setStatus("STATUS_SAVED"))
    if (response.error) {
      return false
    }

    //Update the store
    const risk = state.physicalRisk.list.find((x) => x.id === PhysicalRiskId)
    if (risk) {
      dispatch(
        physicalRiskStore.actions.edit({
          key: "populationIds",
          idInList: PhysicalRiskId,
          value: risk.populationIds.concat([PopulationId])
        })
      )
    }

    return true
  }
)

/**
 * edit risk
 */
export const edit = createAsyncThunk(
  "physicalRisk/edit",
  async (
    { key, value, isAttributes }: { key: keyof PhysicalRisk; value: any; isAttributes?: boolean },
    { dispatch }
  ) => {
    if (isAttributes) {
      dispatch(
        physicalRiskStore.actions.edit({
          key: "attributes",
          nestedKey: key,
          nestedValue: value
        })
      )
    } else {
      dispatch(physicalRiskStore.actions.edit({ key, value }))
    }
  }
)

/**
 * During the visit auto select the first action that in not done
 */
export const visit = createAsyncThunk(
  "physicalRisk/visit",
  async (risk: PhysicalRisk | null, { dispatch, getState }) => {
    dispatch(physicalRiskStore.actions.setActive(risk))
    if (risk) {
      const state = getState() as RootState
      const nextAction = preventionActionStore.selectors.listForRisk(state, risk.id).find((x) => !x.dateDone)
      dispatch(preventionActionStore.actions.setActive(nextAction))
    }
  }
)

/**
 * Remove population from one risk
 */
export const removePopulation = createAsyncThunk(
  "physicalRisk/remove-population",
  async (
    { PopulationId, PhysicalRiskId }: { PopulationId: string; PhysicalRiskId: string },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState
    dispatch(physicalRiskStore.actions.setStatus("STATUS_SAVING"))

    //Send data
    const response = await fetchBackend({
      url: "/physicalRisk/remove-population",
      method: "POST",
      data: {
        PopulationId,
        PhysicalRiskId
      }
    })

    //Handle response
    dispatch(physicalRiskStore.actions.setStatus("STATUS_SAVED"))
    if (response.error) {
      return false
    }

    //Update the store
    const risk = state.physicalRisk.list.find((x) => x.id === PhysicalRiskId)
    if (risk) {
      dispatch(
        physicalRiskStore.actions.edit({
          key: "populationIds",
          idInList: PhysicalRiskId,
          value: risk.populationIds.filter((id) => id !== PopulationId)
        })
      )
    }

    return true
  }
)

/**
 * Remove site from one risk
 */
export const removeSite = createAsyncThunk(
  "physicalRisk/remove-site",
  async ({ SiteId, PhysicalRiskId }: { SiteId: string; PhysicalRiskId: string }, { dispatch, getState }) => {
    const state = getState() as RootState
    dispatch(physicalRiskStore.actions.setStatus("STATUS_SAVING"))

    const response = await fetchBackend({
      url: "/physicalRisk/remove-site",
      method: "POST",
      data: {
        SiteId,
        PhysicalRiskId
      }
    })

    dispatch(physicalRiskStore.actions.setStatus("STATUS_SAVED"))
    if (response.error) {
      return false
    }

    const risk = state.physicalRisk.list.find((x) => x.id === PhysicalRiskId)
    if (risk) {
      dispatch(
        physicalRiskStore.actions.edit({
          key: "siteIds",
          idInList: PhysicalRiskId,
          value: risk.siteIds.filter((id) => id !== SiteId)
        })
      )
    }

    return true
  }
)

/**
 * Update the photo for the relation with the site
 */
export const updatePhoto = createAsyncThunk(
  "physicalRisk/update-photo",
  async (photoId: string | null, { dispatch }) => {
    dispatch(physicalRiskStore.actions.edit({ key: "photoId", value: photoId }))
  }
)
